// src/components/PlanstandQrDialog.jsx
// QR-Code zu genau einem Planstand-Link (US-WEBP-003).
//
// Der Code wird clientseitig erzeugt und nicht bei einem QR-Dienst geholt: der Link
// landet auf gedruckten Plänen, und ein externer Dienst würde damit jeden erzeugten
// Planstand-Link mitprotokollieren.
//
// Immer schwarz auf weiss, unabhängig vom Theme — der Code wird gedruckt, und
// invertierte Codes lesen viele Kameras nicht.
import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

// Kantenlänge des erzeugten PNG. Bewusst gross: der Code wird auf Plänen bis A0
// platziert, ein 256px-Bild wäre dort unscharf.
const PNG_GROESSE = 1024;

// Fehlerkorrektur M (~15%): verträgt Knick und Schmutz auf der Baustelle, ohne den
// Code unnötig dicht zu machen. Der Link ist kurz, die QR-Version bleibt niedrig.
const FEHLERKORREKTUR = "M";

const PlanstandQrDialog = ({ url, dateiname, kopfzeile, veraltet, onClose }) => {
  const [dataUrl, setDataUrl] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [meldung, setMeldung] = useState(null);
  const meldungTimer = useRef(null);

  useEffect(() => {
    let abgebrochen = false;
    QRCode.toDataURL(url, {
      errorCorrectionLevel: FEHLERKORREKTUR,
      width: PNG_GROESSE,
      margin: 2,
      color: { dark: "#000000", light: "#ffffff" },
    })
      .then((erzeugt) => {
        if (!abgebrochen) setDataUrl(erzeugt);
      })
      .catch((err) => {
        if (!abgebrochen) {
          setFehler(err?.message || "QR-Code konnte nicht erzeugt werden.");
        }
      });
    return () => {
      abgebrochen = true;
    };
  }, [url]);

  useEffect(() => {
    const aufTaste = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", aufTaste);
    return () => window.removeEventListener("keydown", aufTaste);
  }, [onClose]);

  useEffect(() => () => clearTimeout(meldungTimer.current), []);

  const melden = (text) => {
    setFehler(null);
    setMeldung(text);
    clearTimeout(meldungTimer.current);
    meldungTimer.current = setTimeout(() => setMeldung(null), 1800);
  };

  const herunterladen = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = dateiname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    melden(`Heruntergeladen: ${dateiname}`);
  };

  const bildKopieren = async () => {
    if (!dataUrl) return;
    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error(
          "Dieser Browser legt keine Bilder in die Zwischenablage — bitte das PNG herunterladen."
        );
      }
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      melden("Bild in der Zwischenablage");
    } catch (err) {
      setMeldung(null);
      setFehler(err?.message || "Bild konnte nicht kopiert werden.");
    }
  };

  const linkKopieren = async () => {
    try {
      await navigator.clipboard.writeText(url);
      melden("Link kopiert");
    } catch {
      setMeldung(null);
      setFehler("Link konnte nicht kopiert werden — bitte markieren und mit Strg+C kopieren.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="planstand-qr"
        role="dialog"
        aria-modal="true"
        aria-label={`QR-Code ${kopfzeile}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="planstand-qr__titel">QR-Code</h3>
        <p className="planstand-qr__plan">{kopfzeile}</p>

        {veraltet && (
          <p className="planstand-qr__warnung">
            Dieser Indexstand gilt als veraltet. Der Code funktioniert, führt aber zum
            Hinweis, dass ein neuerer Stand vorliegt.
          </p>
        )}

        <div className="planstand-qr__bild">
          {dataUrl ? (
            <img src={dataUrl} alt={`QR-Code auf ${url}`} width={240} height={240} />
          ) : (
            <span className="planstand-qr__leer">
              {fehler ? "QR-Code nicht verfügbar" : "wird erzeugt…"}
            </span>
          )}
        </div>

        <code className="planstand-qr__url">{url}</code>
        <p className="planstand-qr__datei">Dateiname: {dateiname}</p>

        {fehler && (
          <p className="planstand-qr__fehler" onClick={() => setFehler(null)}>
            {fehler}
          </p>
        )}
        {meldung && <p className="planstand-qr__meldung">{meldung}</p>}

        <div className="planstand-qr__aktionen">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!dataUrl}
            onClick={herunterladen}
          >
            PNG herunterladen
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!dataUrl}
            onClick={bildKopieren}
          >
            Bild kopieren
          </button>
          <button type="button" className="btn btn-secondary" onClick={linkKopieren}>
            Link kopieren
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Schliessen
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanstandQrDialog;
